import { ANALYTICS_EVENTS } from './eventNames'

function isPromiseLike(value) {
  return Boolean(value) && typeof value.then === 'function'
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]'
}

function scheduleTask(task) {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(task)
    return
  }

  Promise.resolve().then(task).catch(() => {})
}

function sanitizeValue(value) {
  if (value === undefined) return undefined
  if (value === null) return null
  if (typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }
  if (typeof value === 'bigint') {
    return value.toString()
  }
  if (typeof value === 'function' || typeof value === 'symbol') {
    return undefined
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString()
  }
  if (value instanceof Error) {
    return sanitizeProperties({
      name: value.name,
      message: value.message,
    })
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeValue(item))
      .filter((item) => item !== undefined)
  }
  if (isPlainObject(value)) {
    return sanitizeProperties(value)
  }

  return undefined
}

function sanitizeProperties(properties = {}) {
  return Object.entries(properties).reduce((accumulator, [key, value]) => {
    const sanitizedValue = sanitizeValue(value)
    if (sanitizedValue !== undefined) {
      accumulator[key] = sanitizedValue
    }
    return accumulator
  }, {})
}

function createEvent(name, properties = {}, metadata = {}) {
  return Object.freeze({
    name,
    properties: Object.freeze(sanitizeProperties(properties)),
    metadata: Object.freeze({
      source: metadata.source ?? 'app',
      timestamp: metadata.timestamp ?? Date.now(),
    }),
  })
}

function defaultErrorReporter({ handlerName, lifecycle, error, eventName }) {
  if (process.env.NODE_ENV === 'production') {
    return
  }

  const scope = eventName ? ` for "${eventName}"` : ''
  // Analytics must never interrupt the product flow, so errors are contained here.
  console.warn(`[event-service] ${handlerName} failed during ${lifecycle}${scope}.`, error)
}

export class EventService {
  constructor({ onError = defaultErrorReporter } = {}) {
    this.onError = onError
    this.handlers = new Map()
  }

  registerHandler(handler) {
    if (!handler || !handler.name || typeof handler.handle !== 'function') {
      this.onError({
        handlerName: handler?.name ?? 'unknown',
        lifecycle: 'register',
        error: new Error('Analytics handler must provide a stable name and a handle(event) function.'),
      })
      return () => {}
    }

    this.handlers.set(handler.name, handler)
    this.invokeSafely(handler, 'initialize')
    return () => this.unregisterHandler(handler.name)
  }

  unregisterHandler(handlerName) {
    const handler = this.handlers.get(handlerName)
    if (!handler) return

    this.invokeSafely(handler, 'teardown')
    this.handlers.delete(handlerName)
  }

  dispatch(name, properties = {}, metadata = {}) {
    const event = createEvent(name, properties, metadata)

    this.handlers.forEach((handler) => {
      scheduleTask(() => {
        this.invokeSafely(handler, 'handle', event)
      })
    })

    return event
  }

  trackPageView(properties = {}, metadata = {}) {
    return this.dispatch(ANALYTICS_EVENTS.PAGE_VIEW, properties, {
      source: 'router',
      ...metadata,
    })
  }

  trackPurchase(properties = {}, metadata = {}) {
    return this.dispatch(ANALYTICS_EVENTS.PURCHASE, properties, {
      source: 'commerce',
      ...metadata,
    })
  }

  flushAll() {
    this.handlers.forEach((handler) => {
      this.invokeSafely(handler, 'flush')
    })
  }

  invokeSafely(handler, lifecycle, event) {
    const lifecycleMethod = handler[lifecycle]
    if (typeof lifecycleMethod !== 'function') return

    try {
      const result = lifecycleMethod.call(handler, event)
      if (isPromiseLike(result)) {
        result.catch((error) => {
          this.onError({
            handlerName: handler.name,
            lifecycle,
            error,
            eventName: event?.name,
          })
        })
      }
    } catch (error) {
      this.onError({
        handlerName: handler.name,
        lifecycle,
        error,
        eventName: event?.name,
      })
    }
  }
}

export const eventService = new EventService()
